import styled, { keyframes } from "styled-components";

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const Card = styled.div`
  background: var(--surface);
  border-radius: 20px;
  width: 100%;

  max-width: ${(props) => (props.$offMaxWidth ? "100%" : "420px")};
  /* Kao flex/grid item ne sme da ima implicitni min-width: auto,
     inače ga neprelomivi sadržaj može raširiti van ekrana */
  min-width: 0;
  position: relative;
  transition: all 0.4s ease;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  box-shadow: var(--shadow-soft);
  animation: ${(props) => (props.$animate ? scaleIn : "none")} 0.6s ease
    forwards;
  opacity: 1;
  display: flex;
  flex-direction: column;
  cursor: ${(props) => (props.$click ? "pointer" : "default")};

  ${(props) =>
    props.$variant === "wide" &&
    `
    flex-direction: row;
    max-width: ${props.$offMaxWidth ? "100%" : "980px"};
    align-items: stretch;
  `}

  @media (max-width: 768px) {
    max-width: 100%;
    ${(props) => props.$variant === "wide" && `flex-direction: column;`}
  }
`;

export const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(to top, var(--primary), transparent);
  }

  ${(props) =>
    props.$variant === "wide" &&
    `
    width: 46%;
    height: auto;
    align-self: stretch;
    flex-shrink: 0;

    img { object-position: center; }
    &::after { display: none; }

    @media (max-width: 768px) {
      width: 100%;
      aspect-ratio: 3 / 2;
    }
  `}
`;

// Status badge preko thumbnail-a (mycourses) — ne produžava karticu u visinu
export const ImageStatusBadge = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.93);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  color: ${(props) =>
    props.$variant === "expired"
      ? "#c62828"
      : props.$variant === "pending"
        ? "#b45309"
        : props.$variant === "approved"
          ? "#2e7d32"
          : "var(--primary)"};

  svg {
    font-size: 14px;
    flex-shrink: 0;
  }
`;

export const CardContent = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;

  ${(props) =>
    props.$variant === "wide" &&
    `
    justify-content: center;
    gap: 14px;
    min-width: 0;
    padding: 26px 30px;

    & > p {
      -webkit-line-clamp: 2;
    }

    & > button {
      align-self: flex-start;
      width: auto;
      margin-top: 6px;
      padding: 13px 30px;
    }
  `}
`;

export const Badge = styled.span`
  display: inline-block;
  align-self: flex-start;
  padding: 6px 16px;
  background: var(--gradient-primary);
  color: var(--surface);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: "Source Code Pro", monospace;
`;

export const Title = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  text-transform: uppercase;
  margin: 0;
`;

export const Subtitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
  text-transform: uppercase;
  margin: 0;
  margin-top: -20px;
  opacity: 0.65;
`;

export const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 15px;
  margin: 0;
  /* Ujednačena visina opisa — uvek max 3 linije */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-top: 1px solid var(--accent-light);
  border-bottom: 1px solid var(--accent-light);

  ${(props) =>
    props.$variant === "wide" &&
    `
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 22px;
    padding: 14px 0;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  `}
`;

export const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 14px;

  svg {
    color: var(--primary);
    font-size: 16px;
    min-width: 16px;
  }

  span {
    line-height: 1.4;
    flex: 1;
    min-width: 0;
    /* Tekst sme da se prelomi (max 2 reda) — nowrap je na mobilnom
       razvlačio karticu šire od ekrana jer red ne može da se slomi */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 10px 0;
  flex-direction: row;
  flex-wrap: wrap;

  .original-price {
    text-decoration: line-through;
    color: var(--text-muted);
    font-size: 18px;
    font-weight: 500;
  }

  .current-price {
    font-size: 36px;
    font-weight: 800;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-family: "Source Code Pro", monospace;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

export const CTAButton = styled.button`
  /* flex: 0 0 auto + margin-top: auto → dugme zadržava prirodnu visinu (ne
     rasteže se kad kartica ima manje sadržaja) i ostaje prikačeno uz dno. */
  flex: 0 0 auto;
  margin-top: auto;
  background: var(--gradient-primary);
  color: var(--surface);
  padding: 15px;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  font-family: inherit;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;

  &:hover {
    background: var(--accent-dark);
    color: var(--surface);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }

  &.added {
    background: var(--success);

    &:hover {
      background: var(--success);
    }
  }

  &.mycourses-btn {
    background: var(--primary);
    /* flex-grow: 0 sprečava da dugme upije višak visine kod jednakih kartica.
       Višak prostora ide iznad progres bloka (marginTop: auto na progresu),
       pa progres i dugme stoje zajedno uz dno */
    flex: 0 0 auto;
    margin-top: 0;

    &:hover {
      background: var(--accent);
    }
  }
`;

export const CertificateStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  margin: 8px 0;

  &.pending {
    background: rgba(255, 165, 0, 0.1);
    color: #ff8c00;
    border: 1px solid rgba(255, 165, 0, 0.3);
  }

  &.approved {
    background: rgba(76, 175, 80, 0.1);
    color: #4caf50;
    border: 1px solid rgba(76, 175, 80, 0.3);
  }

  &.rejected {
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
    border: 1px solid rgba(244, 67, 54, 0.3);
  }

  svg {
    font-size: 14px;
  }

  svg {
    animation: ${(props) =>
      props.$status ? "hourglassFlip 1.8s ease-in-out infinite" : "none"};
    transform-style: preserve-3d;
  }

  @keyframes hourglassFlip {
    0% {
      transform: rotateX(0deg);
    }
    45% {
      transform: rotateX(180deg);
    }
    55% {
      transform: rotateX(180deg);
    }
    60% {
      transform: rotateX(180deg);
    }
    100% {
      transform: rotateX(360deg);
    }
  }
`;
